// src/pages/OrganisationVerification.js
import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Tag,
  Input,
  Select,
  Space,
  Avatar,
  message,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";

const { Option } = Select;

function OrganisationVerification() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: "all",
    search: "",
  });

  useEffect(() => {
    fetchVerifications();
  }, [filters.status]); // Recharger quand le statut change

  useEffect(() => {
    filterData();
  }, [verifications, filters.search]); // Filtrer quand la recherche change

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const endpoint =
        filters.status === "all"
          ? "/api/admin/organisation-verification/all"
          : `/api/admin/organisation-verification/${filters.status}`;

      console.log("Fetching:", `${process.env.REACT_APP_API_URL}${endpoint}`);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors du chargement");
      }

      const data = await response.json();
      console.log("Data received:", data.data);
      setVerifications(data.data);
      setFilteredData(data.data); // Important: initialiser filteredData aussi
    } catch (error) {
      message.error("Erreur lors du chargement des vérifications");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = verifications;

    // Filtrer par recherche
    if (filters.search) {
      filtered = filtered.filter(
        (item) =>
          item.organisation?.name
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          item.organisation?.owner?.fullname
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          item.organisationId
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()),
      );
    }

    setFilteredData(filtered);
  };

  const handleStatusChange = (value) => {
    setFilters({ ...filters, status: value });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "En attente",
      },
      approved: {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Approuvé",
      },
      rejected: {
        color: "red",
        icon: <CloseCircleOutlined />,
        text: "Rejeté",
      },
      not_submitted: {
        color: "default",
        icon: <FileTextOutlined />,
        text: "Non soumis",
      },
    };

    const config = statusConfig[status] || statusConfig.not_submitted;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getProgressTag = (record) => {
    const hasRccm = !!record.rccmUrl;
    const hasDfe = !!record.dfeUrl;

    if (hasRccm && hasDfe) {
      return <Tag color="success">Complet</Tag>;
    }
    if (hasRccm || hasDfe) {
      return <Tag color="warning">Partiel</Tag>;
    }
    return <Tag>Aucun document</Tag>;
  };

  const columns = [
    {
      title: "Organisation",
      dataIndex: ["organisation", "name"],
      key: "organisation",
      render: (name, record) => (
        <Space>
          <Avatar style={{ backgroundColor: "#1890ff" }}>
            {name?.charAt(0).toUpperCase() || "?"}
          </Avatar>
          <div>
            <div style={{ fontWeight: "500" }}>{name || "N/A"}</div>
            <div style={{ fontSize: "12px", color: "#999" }}>
              {record.organisationId || "N/A"}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Propriétaire",
      dataIndex: ["organisation", "owner", "fullname"],
      key: "owner",
      render: (fullname, record) => (
        <div>
          <div>{fullname || "N/A"}</div>
          <div style={{ fontSize: "12px", color: "#999" }}>
            {record.organisation?.owner?.phone || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Documents",
      key: "progress",
      render: (_, record) => getProgressTag(record),
    },
    {
      title: "Rejets",
      dataIndex: "rejectionCount",
      key: "rejectionCount",
      render: (count) => (
        <Tag color={count >= 3 ? "red" : count > 0 ? "orange" : "default"}>
          {count || 0}/3
        </Tag>
      ),
    },
    {
      title: "Date de soumission",
      dataIndex: "submittedAt",
      key: "submittedAt",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("fr-FR") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        console.log("Record organisationId:", record.organisationId); // Debug
        return (
          <Link to={`/organisation-verification/${record.organisationId}`}>
            <Button type="primary" icon={<EyeOutlined />} size="small">
              Détails
            </Button>
          </Link>
        );
      },
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="Vérifications d'Organisations (KYB)"
            extra={
              <Space>
                <Input
                  placeholder="Rechercher..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  style={{ width: 200 }}
                />
                <Select
                  value={filters.status}
                  onChange={handleStatusChange}
                  style={{ width: 150 }}
                >
                  <Option value="all">Tous</Option>
                  <Option value="pending">En attente</Option>
                  <Option value="approved">Approuvés</Option>
                  <Option value="rejected">Rejetés</Option>
                  <Option value="not_submitted">Non soumis</Option>
                </Select>
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowKey="id"
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `Total: ${total} vérifications`,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default OrganisationVerification;
